import { html, ref, repeat, when } from '@microsoft/fast-element';
import { staticallyCompose } from '@microsoft/fast-foundation';

import type { Root } from './root.js';
import { SiteColors } from '../style.js';

import quote from '/images/quote.svg?raw';

export default html<Root>`
	<site-header
    view=${x => x.contentIsIntersecting ? 'scroll' : 'default'}
    ${ref('header')}
  >
    <adaptive-anchor
      appearance=${x => !x.contentIsIntersecting ? "accent" : "lightweight"}
      href="#get-connected"
      @click=${(x, c) => x.handleClick()}
      aria-label=${x => x.isMobileScreen ? "Get Connected" : null}
      ${ref('headerCTA')}
    >
      <svg slot="${x => !x.isMobileScreen ? "start" : null}" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16"><path fill="currentColor" d="M3 3a2 2 0 1 1 4 0a2 2 0 0 1-4 0Zm6.779 1.584l.042.032a2 2 0 1 0-.042-.032ZM6.268 6A2 2 0 1 1 9.73 7.998A2 2 0 0 1 6.268 6ZM2.5 6h2.67a3.013 3.013 0 0 0 .594 3H5.5a2.501 2.501 0 0 0-2.355 1.658a3.733 3.733 0 0 1-.933-.543C1.46 9.51 1 8.616 1 7.5A1.5 1.5 0 0 1 2.5 6Zm8 3a2.5 2.5 0 0 1 2.354 1.658c.34-.139.655-.32.934-.543C14.54 9.51 15 8.616 15 7.5A1.5 1.5 0 0 0 13.5 6h-2.67c.11.313.17.65.17 1a2.99 2.99 0 0 1-.764 2h.264Zm1.387 1.928c.073.176.113.37.113.572c0 1.116-.459 2.01-1.212 2.615C10.047 14.71 9.053 15 8 15c-1.053 0-2.047-.29-2.788-.885C4.46 13.51 4 12.616 4 11.5A1.496 1.496 0 0 1 5.5 10h5a1.5 1.5 0 0 1 1.387.928Z"/></svg>
      ${when(x => !x.isMobileScreen, html`Get Connected`)}
    </adaptive-anchor>
  </site-header>

	<main ${ref('mainContentArea')}>
		<section class="hero wide">
      <section-header type="hero">
        <img
          alt="A person's finger is touching a network of dots."
          slot="background" 
          src="/images/photo-hero-connection.lg.webp"
          srcset="/images/photo-hero-connection.sm.webp 600w,
                  /images/photo-hero-connection.md.webp 1200w,
                  /images/photo-hero-connection.lg.webp 1800w"
          sizes="(max-width: 768px) 100vw,
                 (max-width: 1024px) 100vw,
                 1920px"
        />
        <div class="hero-content">
          <call-to-action>
            <h2 slot="heading">Driving Value Together</h2>
            <p>Enabling our customers to reach their full potential with AI, automation, and beautifully crafted products that deliver immediate value.</p>
            <adaptive-anchor
              appearance=${x => x.colorScheme === 'dark' ? 'accent' : null}
              slot="action"
              href="#get-connected"
              @click=${(x, c) => x.handleClick()}
              ${ref('heroCTA')}
            >
              <svg slot="start" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16"><path fill="currentColor" d="M3 3a2 2 0 1 1 4 0a2 2 0 0 1-4 0Zm6.779 1.584l.042.032a2 2 0 1 0-.042-.032ZM6.268 6A2 2 0 1 1 9.73 7.998A2 2 0 0 1 6.268 6ZM2.5 6h2.67a3.013 3.013 0 0 0 .594 3H5.5a2.501 2.501 0 0 0-2.355 1.658a3.733 3.733 0 0 1-.933-.543C1.46 9.51 1 8.616 1 7.5A1.5 1.5 0 0 1 2.5 6Zm8 3a2.5 2.5 0 0 1 2.354 1.658c.34-.139.655-.32.934-.543C14.54 9.51 15 8.616 15 7.5A1.5 1.5 0 0 0 13.5 6h-2.67c.11.313.17.65.17 1a2.99 2.99 0 0 1-.764 2h.264Zm1.387 1.928c.073.176.113.37.113.572c0 1.116-.459 2.01-1.212 2.615C10.047 14.71 9.053 15 8 15c-1.053 0-2.047-.29-2.788-.885C4.46 13.51 4 12.616 4 11.5A1.496 1.496 0 0 1 5.5 10h5a1.5 1.5 0 0 1 1.387.928Z"/></svg>
              Get Connected
            </adaptive-anchor>
          </call-to-action>
        </div>
      </section-header>
    </section>

    <section class="our-journey">
      <section-header>
        <h2>
          <site-text color=${SiteColors.brandGreen}>Our Journey</site-text>
        </h2>
      </section-header>
      <hero-card
        img-src="/images/photo-experience-the-otio-way.sm.webp"
        img-alt="A business woman is pointing at a digital screen."
      >
        <h3 class="highlighted">
          <site-text>In the Beginning</site-text>
        </h3>
        <p>Since 2015, Translational Analytics & Statistics has been revolutionizing kidney stone treatment through the power of data. What started as a mission to understand complex medical relationships between providers, patients, and outcomes has evolved into the world's most comprehensive kidney stone research platform.</p>
        <p>We built the International Stone Registry from the ground up, with more than 300,000 cases and millions of data points, TAS PRO is the foundation for breakthrough insights leading to superior patient outcomes that were previously impossible to discover.</p>
     
      </hero-card>
      <hero-card
        img-src="/images/photo-experience-proven-leadership.sm.webp"
        img-alt="A group of business people shaking hands in front of a crowd."
        reverse
      >
        <h3 class="highlighted">
          <site-text>Pioneers in Translational Analytics</site-text>
        </h3>
        <p>Our journey began with a simple belief: that better data leads to better patient outcomes. We pioneered statistical analysis methods specifically designed for kidney stone treatments, helping healthcare professionals move beyond guesswork to evidence-based protocols.  As our registry grew, so did our impact.</p>
        <p>We became trusted partners to medical practices nationwide, sharing critical insights that elevated treatment standards across the industry. Our data-driven approach transformed how doctors approach each case, creating personalized treatment protocols that significantly reduce the need for repeat procedures.</p>
      </hero-card>
      <hero-card
        img-src="/images/photo-experience-management-advisors.sm.webp"
        img-alt="A group of people sitting around a table with a light bulb."
      >
        <h3 class="highlighted">
          <site-text>Early Adopters of Artificial Intelligence</site-text>
        </h3>
        <p>Today, we serve more than 600 healthcare facilities, empowering healthcare professionals with the insights they need to deliver superior patient care coupled with valuable economic analysis to help them build stronger practices.</p>
        <p>With a history in advanced data analytics and as early adopters of artificial intelligence, our core mission remains: to provide in-depth reporting that empowers service providers and care givers with the information needed to improve treatment efficiency, patient outcomes, and operating costs.</p>
      </hero-card>
      <hero-card
        img-src="/images/photo-experience-accelerating-profitability.sm.webp"
        img-alt="A businessman in a suit is pointing at a graph."
        reverse
      >
        <h3 class="highlighted">
          <site-text>Delivering Excellence</site-text>
        </h3>
        <p>In 2025 we launched <strong>TAS PRO</strong> to bridge the gap between research, operational performance, and real-world mobile services delivery — driving excellence in Mobile Services. Our <strong>PRO</strong>fessional distinction is based on our experience from where we started in healthcare analytics, where we are now with AI, and our desire to widen our impact across industries.</p>
        </hero-card>
    </section>

		<section class="services">
      <section-header>
        <h2>
          <site-text color=${SiteColors.brandGreen}>Our Platform</site-text>
        </h2>
        <p>TAS PRO combines the simplicity of beautifully crafted user experiences built on our proprietary Otio Platform into the clinicians' hands to maximize your team's operational performance by turning analytic data into predictive intelligence and actionable insights. Our back-office autonomous agents and automated workflows increase profitability throughout your entire organization.</p>
        <p>Our seamless and inductive user experiences nearly eliminate the need for costly traditional training and support resources. TAS PRO is built from the ground up with a quantum-secure network and our AI-first approach delivering for durability, extensibility, scalability, and performance.</p>
      </section-header>
      <platform-diagram></platform-diagram>
    </section>

		<section class="expertise">
      <section-header>
        <h2>
          <site-text color=${SiteColors.brandGreen}>Our Capabilities</site-text>
        </h2>
      </section-header>
      <card-deck class="expertise-card-deck">
        <expand-card>
          <span slot="heading"><span class="highlighted">Predictive</span> AI</span>
          <img
            src="/images/photo-services-brand-identity-design.sm.webp"
            alt="A person working at a desk with a computer and pens."
          />
          <p slot="body">TAS PRO uses historical data and machine learning algorithms to forecast future outcomes, trends, and behaviors.</p>
        </expand-card>
        <expand-card>
          <span slot="heading"><span class="highlighted">Agentic</span> AI</span>
          <img
            src="/images/photo-services-digital-strategy-marketing.sm.webp"
            alt="A person holding a phone with a lot of different apps flying out of it."
          />
          <p slot="body">
          TAS PRO uses autonomous agents to achieve goals, make decisions, and take actions without constant human oversight.
          </p>
        </expand-card>
        <expand-card>
          <span slot="heading"><span class="highlighted">Generative</span> AI</span>
          <img
            src="/images/photo-services-mobile-web-solutions.sm.webp"
            alt="A desk with a monitor and a computer connected to the internet."
          />
          <p slot="body">
          TAS PRO systems create, restore, or improve content, processes, or systems by generating new outputs from existing data.
          </p>
        </expand-card>
        <expand-card>
          <span slot="heading"><span class="highlighted">RAG</span> AI</span>
          <img
            src="/images/photo-services-enterprise-solutions.sm.webp"
            alt="A businessman is using a tablet with a graph on it."
          />
          <p slot="body">
          Combines information retrieval with generative AI to produce more accurate, up-to-date, and contextually relevant responses.
          </p>
        </expand-card>
      </card-deck>
    </section>

    <section ?hidden=${x => !x.customerData.length} class="testimonials">
      <section-header>
        <h2>
          <site-text color=${SiteColors.brandGreen}>Our Customers</site-text>
        </h2>
      </section-header>

      <site-carousel
        active-id="item-2"
        view-mode=${x => x.isMobileScreen ? 'mobile' : null}
      >
        ${repeat(x => x.customerData, html`
          <adaptive-card id="item-${(x, c) => c.index + 1}">
            <div class="quote">
              ${when(x => x.logo,
                html`
                  <img class="logo" src=${x => x.logo} alt="${x => x.company} logo" />
                `,
                html`
                  <h3>${x => x.company}</h3>
                `
              )}
              <blockquote>
                <p class="text">${x => x.quote}</p>
                <span class="graphic">${staticallyCompose(quote)}</span>
              </blockquote>
            </div>
            <div class="customer">
              <div class="avatar">
                <div class="image">
                  <img src=${x => x.image} alt="${x => x.name}, ${x => x.title} at ${x => x.company}" />
                </div>
                <b>${x => x.name}</b>
                <div>${x => x.title}</div>
              </div>
              ${when(x => x.caseStudyLink, html`
                <a href=${x => x.caseStudyLink}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="2em" height="2em" viewBox="0 0 16 16"><path fill="currentColor" d="M6.5 5.82v4.36c0 .25.274.403.487.273l3.259-1.992a.54.54 0 0 0 0-.922l-3.26-1.991a.32.32 0 0 0-.486.273M4.5 3A2.5 2.5 0 0 0 2 5.5v5A2.5 2.5 0 0 0 4.5 13h7a2.5 2.5 0 0 0 2.5-2.5v-5A2.5 2.5 0 0 0 11.5 3zM3 5.5A1.5 1.5 0 0 1 4.5 4h7A1.5 1.5 0 0 1 13 5.5v5a1.5 1.5 0 0 1-1.5 1.5h-7A1.5 1.5 0 0 1 3 10.5z"/></svg>
                  View Case Study
                </a>
              `)}
            </div>
          </adaptive-card>
        `, { positioning: true })}
      </site-carousel>
    </section>

    <section class="principles">
      <section-header>
        <h2>
          <site-text color=${SiteColors.brandGreen}>Our Principles</site-text>
        </h2>
      </section-header>
      <card-deck class="principles-card-deck">
        <simple-card>
          <img
            src="/images/photo-principles-create-clarity.sm.webp"
            alt="A pair of sunglasses reflected in a lake."
          />
          <p slot="body">
            <span class="highlighted">Create</span> Clarity
          </p>
        </simple-card>
        <simple-card>
          <img
            src="/images/photo-principles-generate-energy.sm.webp"
            alt="An image of a bright light coming out of a dark space."
          />
          <p slot="body">
            <span class="highlighted">Generate</span> Energy
          </p>
        </simple-card>
        <simple-card>
          <img
            src="/images/photo-principles-deliver-success.sm.webp"
            alt="A man looking at a business report on a screen."
          />
          <p slot="body">
            <span class="highlighted">Deliver</span> Success
          </p>
        </simple-card>
      </card-deck>
    </section>

		<section class="contact" ${ref('contact')}>
      <section-header>
        <h2>
          <site-text color=${SiteColors.brandGreen}>Get Connected</site-text>
        </h2>
        <p slot="body">
          We are excited to connect with you and learn more about how TAS can help! Fill out the form below, and we'll get back to you as soon as possible.
        </p>
      </section-header>
      <contact-form></contact-form>
    </section>
	</main>

	<site-footer></site-footer>
`;